/* eslint-disable no-console */
import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { actualizarCreditoOtorgado, crearCreditoConCuotas, generarCuotas } from "../../utils/creditos";
import { getLocale } from "../../utils/getLocale";
import styles from "./Creditos.module.scss";

const TIPO_ACREEDOR_OPCIONES = [
  { value: "banco", label: "Banco" },
  { value: "privado", label: "Privado" },
  { value: "casa_matriz", label: "Casa matriz" },
  { value: "intercompany", label: "Intercompany" },
  { value: "socio", label: "Socio" },
];

const DIRECCION_OPCIONES = [
  { value: "tomado", label: "Tomado — deuda propia (por pagar)" },
  { value: "otorgado", label: "A favor — por cobrar / a recuperar (ej. fianza)" },
];

const PERIODICIDAD_OPCIONES = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
  { value: "unica", label: "Pago único" },
];

// Monedas por país, alineadas con las del proyecto (CLP, PEN, USD, UF) y con lo que el conversor
// de caja sabe cruzar por fecha: Chile CLP/UF/USD (UF→CLP vía getUFValue), Perú PEN/USD. UF es
// Chile-only y PEN Perú-only a propósito — ofrecer UF↔PEN o PEN-en-Chile no tiene conversión y
// rompería la proyección (raw/0, viola 10.185).
const MONEDAS_POR_PAIS = {
  Chile: ["CLP", "UF", "USD"],
  Perú: ["PEN", "USD"],
};

// Formato de monto para input (sin decimales, miles según locale).
const formatMontoDisplay = (value, pais) => {
  if (value === "" || value == null) return "";
  if (typeof value === "string" && isNaN(Number(value))) return value;
  return Number(value).toLocaleString(getLocale(pais), { maximumFractionDigits: 0 });
};

const parseMontoInput = (displayValue) => {
  const clean = displayValue.replace(/\D/g, "");
  if (!clean) return "";
  const num = parseInt(clean, 10);
  return isNaN(num) ? "" : num;
};

// Modo edición: SOLO créditos 'otorgado' (a favor) llegan acá con `editCredito`. La edición de
// deuda ('tomado') no existe y queda fuera de alcance. En edición la fecha de recupero se toma
// de la ÚNICA cuota (fuente de verdad de la proyección), no del header, por si alguna vez difieren.
const CrearCreditoModal = ({ isOpen, onClose, paisDefault, userId, onSuccess, editCredito = null }) => {
  const esEdicion = !!editCredito;
  const initial = useMemo(() => {
    if (editCredito) {
      const cuota = (editCredito.cuotas || [])[0];
      return {
        direccion: editCredito.direccion || "otorgado",
        nombre: editCredito.nombre || "",
        pais: editCredito.pais || "Chile",
        tipo_acreedor: editCredito.tipo_acreedor || "contraparte",
        acreedor_nombre: editCredito.acreedor_nombre || "",
        monto_original: editCredito.monto_original ?? "",
        moneda: editCredito.moneda || "CLP",
        tasa_interes: "",
        tipo_tasa: "",
        num_cuotas: "1",
        periodicidad: "unica",
        dia_pago: "",
        fecha_primera_cuota: cuota?.fecha_vencimiento || editCredito.fecha_primera_cuota || "",
        monto_cuota_fijo: "",
        cuotas_gracia: 0,
        pago_inicial: "",
        fecha_desembolso: editCredito.fecha_desembolso || "",
        notas: editCredito.notas || "",
      };
    }
    return {
      direccion: "tomado",
      nombre: "",
      pais: paisDefault || "Chile",
      tipo_acreedor: "banco",
      acreedor_nombre: "",
      monto_original: "",
      moneda: paisDefault === "Perú" ? "PEN" : "CLP",
      tasa_interes: "",
      tipo_tasa: "",
      num_cuotas: "",
      periodicidad: "mensual",
      dia_pago: "",
      fecha_primera_cuota: "",
      monto_cuota_fijo: "",
      cuotas_gracia: 0,
      pago_inicial: "",
      fecha_desembolso: "",
      notas: "",
    };
  }, [paisDefault, editCredito]);

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(initial);
  }, [isOpen, initial]);

  const set = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const monedasDisponibles = MONEDAS_POR_PAIS[form.pais] || ["CLP", "USD"];
  const esOtorgado = form.direccion === "otorgado";

  // Preview del schedule (primeras cuotas) para que el usuario vea qué se va a generar.
  const previewCuotas = useMemo(() => {
    if (!form.num_cuotas || !form.fecha_primera_cuota) return [];
    try {
      return generarCuotas({
        num_cuotas: Number(form.num_cuotas),
        periodicidad: form.periodicidad,
        dia_pago: form.dia_pago ? Number(form.dia_pago) : null,
        fecha_primera_cuota: form.fecha_primera_cuota,
        monto_cuota_fijo: form.monto_cuota_fijo === "" ? null : Number(form.monto_cuota_fijo),
        monto_original: Number(form.monto_original) || 0,
        cuotas_gracia: Number(form.cuotas_gracia) || 0,
      });
    } catch {
      return [];
    }
  }, [
    form.num_cuotas,
    form.periodicidad,
    form.dia_pago,
    form.fecha_primera_cuota,
    form.monto_cuota_fijo,
    form.monto_original,
    form.cuotas_gracia,
  ]);

  const validar = () => {
    if (!form.nombre.trim()) return "El nombre es requerido";
    if (esOtorgado) {
      // Crédito a favor (ej. fianza): solo se piden los campos que aplican.
      if (!form.acreedor_nombre.trim()) return "La contraparte es requerida";
      if (!Number(form.monto_original) || Number(form.monto_original) <= 0) return "El monto debe ser mayor a 0";
      if (!form.moneda) return "La moneda es requerida";
      if (!form.fecha_desembolso) return "La fecha de entrega es requerida";
      if (!form.fecha_primera_cuota) return "La fecha esperada de recupero es requerida";
      return null;
    }
    if (!form.acreedor_nombre.trim()) return "El acreedor es requerido";
    if (!form.fecha_desembolso) return "La fecha de inicio / desembolso es requerida";
    if (!Number(form.monto_original) || Number(form.monto_original) <= 0) return "El monto original debe ser mayor a 0";
    if (!form.moneda) return "La moneda es requerida";
    if (!Number(form.num_cuotas) || Number(form.num_cuotas) <= 0) return "El número de cuotas debe ser mayor a 0";
    if (!form.fecha_primera_cuota) return "La fecha de la primera cuota es requerida";
    return null;
  };

  const handleGuardar = async () => {
    const error = validar();
    if (error) {
      toast.error(error);
      return;
    }
    setLoading(true);
    try {
      // Payload común. Para 'otorgado' (a favor) se fuerza la forma canónica de crédito a favor:
      // una única cuota de recupero (num_cuotas=1, periodicidad='unica', fecha_primera_cuota =
      // fecha de recupero). Los campos de deuda que no aplican van neutros (sin tasa, sin gracia).
      // tipo_acreedor es NOT NULL y no se pide en el form a favor → 'contraparte' (valor propio
      // desde la Fase 3; la otra parte del crédito otorgado). `direccion` gobierna el resto
      // (comparado por campo, 10.7).
      const payload = esOtorgado
        ? {
            direccion: "otorgado",
            pais: form.pais,
            nombre: form.nombre.trim(),
            tipo_acreedor: "contraparte",
            acreedor_nombre: form.acreedor_nombre.trim(),
            monto_original: Number(form.monto_original),
            moneda: form.moneda,
            tasa_interes: null,
            tipo_tasa: null,
            num_cuotas: 1,
            periodicidad: "unica",
            dia_pago: null,
            fecha_desembolso: form.fecha_desembolso,
            fecha_primera_cuota: form.fecha_primera_cuota,
            monto_cuota_fijo: null,
            cuotas_gracia: 0,
            pago_inicial: 0,
            notas: form.notas.trim() || null,
            created_by: userId || null,
          }
        : {
            direccion: "tomado",
            pais: form.pais,
            nombre: form.nombre.trim(),
            tipo_acreedor: form.tipo_acreedor,
            acreedor_nombre: form.acreedor_nombre.trim(),
            monto_original: Number(form.monto_original),
            moneda: form.moneda,
            tasa_interes: form.tasa_interes === "" ? null : Number(form.tasa_interes),
            tipo_tasa: form.tipo_tasa || null,
            num_cuotas: Number(form.num_cuotas),
            periodicidad: form.periodicidad,
            dia_pago: form.dia_pago === "" ? null : Number(form.dia_pago),
            fecha_desembolso: form.fecha_desembolso,
            fecha_primera_cuota: form.fecha_primera_cuota,
            monto_cuota_fijo: form.monto_cuota_fijo === "" ? null : Number(form.monto_cuota_fijo),
            cuotas_gracia: Number(form.cuotas_gracia) || 0,
            pago_inicial: form.pago_inicial === "" ? 0 : Number(form.pago_inicial),
            notas: form.notas.trim() || null,
            created_by: userId || null,
          };
      if (esEdicion) {
        // Edición: solo 'otorgado'. Sincroniza header + única cuota (monto/fecha) en la capa de datos.
        await actualizarCreditoOtorgado(editCredito.id, {
          nombre: payload.nombre,
          acreedor_nombre: payload.acreedor_nombre,
          monto_original: payload.monto_original,
          moneda: payload.moneda,
          pais: payload.pais,
          fecha_desembolso: payload.fecha_desembolso,
          fecha_primera_cuota: payload.fecha_primera_cuota,
        });
        toast.success("Crédito a favor actualizado");
      } else {
        await crearCreditoConCuotas(payload);
        toast.success(esOtorgado ? "Crédito a favor creado" : "Crédito creado");
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error guardando crédito:", err);
      toast.error(err?.message || (esEdicion ? "Error al actualizar el crédito" : "Error al crear el crédito"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={loading ? undefined : onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          {esEdicion ? "Editar crédito a favor" : esOtorgado ? "Nuevo crédito a favor" : "Nuevo crédito"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* ── Datos generales ── */}
        <div className={styles.modalSection}>
          <div className={styles.modalSectionTitle}>Datos generales</div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol} style={{ flex: 3 }}>
              <label className={styles.fieldLabel}>Dirección *</label>
              <select
                className="form-select form-select-sm"
                value={form.direccion}
                onChange={(e) => set("direccion", e.target.value)}
                disabled={loading || esEdicion}
              >
                {DIRECCION_OPCIONES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol} style={{ flex: 2 }}>
              <label className={styles.fieldLabel}>Nombre *</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={esOtorgado ? "Fianza oficina Wecast" : "Santander Crédito 420021536244"}
                value={form.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>País</label>
              <select
                className="form-select form-select-sm"
                value={form.pais}
                onChange={(e) => {
                  const nuevoPais = e.target.value;
                  set("pais", nuevoPais);
                  const monedas = MONEDAS_POR_PAIS[nuevoPais] || ["CLP"];
                  if (!monedas.includes(form.moneda)) set("moneda", monedas[0]);
                }}
                disabled={loading}
              >
                <option value="Chile">Chile</option>
                <option value="Perú">Perú</option>
              </select>
            </div>
          </div>
          <div className={styles.fieldRow}>
            {!esOtorgado && (
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Tipo de acreedor</label>
                <select
                  className="form-select form-select-sm"
                  value={form.tipo_acreedor}
                  onChange={(e) => set("tipo_acreedor", e.target.value)}
                  disabled={loading}
                >
                  {TIPO_ACREEDOR_OPCIONES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className={styles.fieldCol} style={{ flex: 2 }}>
              <label className={styles.fieldLabel}>{esOtorgado ? "Contraparte *" : "Acreedor *"}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={esOtorgado ? "Inmobiliaria / arrendador" : "Banco Santander"}
                value={form.acreedor_nombre}
                onChange={(e) => set("acreedor_nombre", e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>
                {esOtorgado ? "Fecha de entrega *" : "Fecha inicio / desembolso *"}
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.fecha_desembolso}
                onChange={(e) => set("fecha_desembolso", e.target.value)}
                disabled={loading}
              />
            </div>
            {esOtorgado ? (
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Fecha esperada de recupero *</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.fecha_primera_cuota}
                  onChange={(e) => set("fecha_primera_cuota", e.target.value)}
                  disabled={loading}
                />
              </div>
            ) : (
              <div className={styles.fieldCol} style={{ flex: 2 }} />
            )}
          </div>
        </div>

        {/* ── Condiciones financieras (a favor: solo monto + moneda) ── */}
        <div className={styles.modalSection}>
          <div className={styles.modalSectionTitle}>{esOtorgado ? "Monto" : "Condiciones financieras"}</div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>{esOtorgado ? "Monto *" : "Monto original *"}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formatMontoDisplay(form.monto_original, form.pais)}
                onChange={(e) => set("monto_original", parseMontoInput(e.target.value))}
                disabled={loading}
              />
            </div>
            <div className={styles.fieldCol}>
              <label className={styles.fieldLabel}>Moneda *</label>
              <select
                className="form-select form-select-sm"
                value={form.moneda}
                onChange={(e) => set("moneda", e.target.value)}
                disabled={loading}
              >
                {monedasDisponibles.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            {!esOtorgado && (
              <>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Tasa interés anual (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control form-control-sm"
                    value={form.tasa_interes}
                    onChange={(e) => set("tasa_interes", e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Tipo de tasa</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.tipo_tasa}
                    onChange={(e) => set("tipo_tasa", e.target.value)}
                    disabled={loading}
                  >
                    <option value="">—</option>
                    <option value="fija">Fija</option>
                    <option value="variable">Variable</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Schedule de pagos (solo deuda; el recupero a favor es cuota única) ── */}
        {!esOtorgado && (
          <div className={styles.modalSection}>
            <div className={styles.modalSectionTitle}>Schedule de pagos</div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>N° cuotas *</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={form.num_cuotas}
                  onChange={(e) => set("num_cuotas", e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Periodicidad</label>
                <select
                  className="form-select form-select-sm"
                  value={form.periodicidad}
                  onChange={(e) => set("periodicidad", e.target.value)}
                  disabled={loading}
                >
                  {PERIODICIDAD_OPCIONES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Día de pago</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  className="form-control form-control-sm"
                  value={form.dia_pago}
                  onChange={(e) => set("dia_pago", e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Fecha primera cuota *</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.fecha_primera_cuota}
                  onChange={(e) => set("fecha_primera_cuota", e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.fieldLabel}>Monto cuota fija</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Vacío = reparto parejo"
                  value={formatMontoDisplay(form.monto_cuota_fijo, form.pais)}
                  onChange={(e) => set("monto_cuota_fijo", parseMontoInput(e.target.value))}
                  disabled={loading}
                />
              </div>
            </div>
            {previewCuotas.length > 0 && (
              <div style={{ fontSize: "0.74rem", color: "#6c757d", marginTop: 4 }}>
                Se generarán {previewCuotas.length} cuotas — primera {previewCuotas[0].fecha_vencimiento}, última{" "}
                {previewCuotas.at(-1).fecha_vencimiento}.
              </div>
            )}
          </div>
        )}

        {/* ── Opciones adicionales ── */}
        <div className={styles.modalSection}>
          <div className={styles.modalSectionTitle}>Opciones adicionales</div>
          <div className={styles.fieldRow}>
            {!esOtorgado && (
              <>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Cuotas de gracia</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm"
                    value={form.cuotas_gracia}
                    onChange={(e) => set("cuotas_gracia", e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldLabel}>Pago inicial</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formatMontoDisplay(form.pago_inicial, form.pais)}
                    onChange={(e) => set("pago_inicial", parseMontoInput(e.target.value))}
                    disabled={loading}
                  />
                </div>
                <div className={styles.fieldCol} />
              </>
            )}
          </div>
          <div>
            <label className={styles.fieldLabel}>Notas</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={form.notas}
              onChange={(e) => set("notas", e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={loading}>
          {loading
            ? esEdicion
              ? "Guardando..."
              : "Creando..."
            : esEdicion
              ? "Guardar cambios"
              : esOtorgado
                ? "Crear crédito a favor"
                : "Crear crédito"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CrearCreditoModal;
